/* eslint-disable no-undef */
import { useEffect, useState, useRef } from "react";
import { useTranslations } from "next-intl";
import { useLocalPartnerNameFor } from "@hooks/use-addresses";
import relayPointMarkerContent from "@utils/relayPointMarkerContent";
import AddressesMap from "./AddressesMap";
import { Button } from "@components/core";

const RelayPointsMap = ({
  referencePlace,
  relayPointsList,
  selectedRelayPoint,
  setSelectedRelayPoint,
  geocoder,
  setGeocoder,
  submitButton = false,
  onSubmit = null,
  hidden = false,
}) => {
  const t = useTranslations("addresses");

  const country = referencePlace?.address_components.find((c) =>
    c.types.includes("country"),
  )?.short_name;
  const localPartnerNameForCountry = useLocalPartnerNameFor(country);

  const [map, setMap] = useState(false);
  const [infoWindow, setInfoWindow] = useState();

  const addressMarker = useRef();
  const relayPointMarkers = useRef({});

  function clearAddressMarker() {
    addressMarker.current?.setMap(null);
    addressMarker.current = null;
  }

  function setAddressMarker(place) {
    clearAddressMarker();
    const addressFormatted = place.formatted_address.split(",");
    let contentString = "";

    for (let i = 0; i < addressFormatted.length; i += 1) {
      contentString += `<p class="address-marker-line">${addressFormatted[i]}</p>`;
    }
    const marker = new google.maps.Marker({
      map,
      position: place.geometry.location,
      icon: {
        url: `/images/${process.env.NEXT_PUBLIC_CUSTOMER}/home.svg`,
        scaledSize: new google.maps.Size(32, 32),
      },
    });

    marker.addListener("click", () => {
      infoWindow.open(map, marker);
      infoWindow.setContent(contentString);
    });

    addressMarker.current = marker;
  }

  function setCenterAddress(place) {
    map.setCenter(place.geometry.location);
    map.setZoom(15);
    setAddressMarker(place);
  }

  function clearRelayPointMarkers() {
    Object.values(relayPointMarkers.current).forEach((marker) => {
      marker.setMap(null);
    });
  }

  function setRelayPointsMarkers(relayPoints) {
    clearRelayPointMarkers();

    const markersTemp = {};
    relayPoints.forEach((relayPoint) => {
      const localPartnerName = localPartnerNameForCountry(relayPoint.carrier_name);

      const marker = new google.maps.Marker({
        map,
        position: {
          lat: parseFloat(relayPoint.latitude),
          lng: parseFloat(relayPoint.longitude),
        },
        icon: {
          url: `/images/${process.env.NEXT_PUBLIC_CUSTOMER}/${localPartnerName}.png`,
          scaledSize: new google.maps.Size(32, 32),
        },
      });

      marker.addListener("click", () => {
        infoWindow.open(map, marker);
        infoWindow.setContent(
          relayPointMarkerContent(relayPoint, submitButton, localPartnerName, t),
        );

        if (selectedRelayPoint?.pickup_id !== relayPoint.pickup_id)
          setSelectedRelayPoint(relayPoint);
      });

      infoWindow?.addListener("closeclick", () => {
        setSelectedRelayPoint(null);
      });

      markersTemp[relayPoint.pickup_id] = marker;
    });

    relayPointMarkers.current = markersTemp;
  }

  useEffect(() => {
    if (map && referencePlace) setCenterAddress(referencePlace);
  }, [referencePlace]);

  useEffect(() => {
    if (map && relayPointsList) setRelayPointsMarkers(relayPointsList);
  }, [relayPointsList]);

  useEffect(() => {
    if (map && selectedRelayPoint) {
      const marker = relayPointMarkers.current[selectedRelayPoint.pickup_id];
      if (marker) {
        const centerPosition = {
          lat: marker.getPosition().lat() + 0.004 * Math.pow(2, 15 - map.getZoom()), // Adjust center to account for infoWindow height
          lng: marker.getPosition().lng(),
        };

        map.panTo(centerPosition);
        google.maps.event.trigger(marker, "click");
      }
    } else if (map && selectedRelayPoint === null) {
      infoWindow.close();
    }
  }, [selectedRelayPoint]);

  return (
    <div hidden={hidden}>
      <AddressesMap
        map={map}
        setMap={setMap}
        geocoder={geocoder}
        setGeocoder={setGeocoder}
        setInfoWindow={setInfoWindow}
      />

      {/* Hidden button to submit from the map */}
      <Button
        id="save-relay-point"
        type="button"
        className="d-none"
        onClick={onSubmit}
      />
    </div>
  );
};

export default RelayPointsMap;
